import { requestIdleCallback, startViewTransition } from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";

/** Matches compact product-grid columns: 3 below md, 4 on tablet, 6 on desktop. */
const PRODUCT_GRID_MD_MEDIA = "(min-width: 768px)";
const PRODUCT_GRID_LG_MEDIA = "(min-width: 1024px)";

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  static observedAttributes = ["initialized"];

  #mdMql = window.matchMedia(PRODUCT_GRID_MD_MEDIA);

  #lgMql = window.matchMedia(PRODUCT_GRID_LG_MEDIA);

  #onMqlChange = () => this.#syncProductGridImagePriority();

  /** Re-applies extended-gallery fetchpriority when the card subtree or attributes change (morph, layout). */
  #extendedGalleryFetchObserver;

  #productGridFetchObserver;

  #productGridFetchObserverGrid;

  #productGridFetchPrioritySyncFrame;

  #onWindowLoadForFetchPriority = () => this.#syncProductGridImagePriority();

  #onWindowResizeForFetchPriority = () => this.#syncProductGridImagePriority();

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "initialized" && newValue === null && this.isConnected) {
      this.setAttribute("initialized", "");
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.#restoreLayout();
    this.#syncProductGridImagePriority();
    requestAnimationFrame(() => {
      this.#syncProductGridImagePriority();
      requestAnimationFrame(() => this.#syncProductGridImagePriority());
    });
    window.addEventListener("load", this.#onWindowLoadForFetchPriority);
    window.addEventListener("resize", this.#onWindowResizeForFetchPriority);
    this.#mdMql.addEventListener("change", this.#onMqlChange);
    this.#lgMql.addEventListener("change", this.#onMqlChange);
    this.setAttribute("initialized", "");
  }

  disconnectedCallback() {
    this.#mdMql.removeEventListener("change", this.#onMqlChange);
    this.#lgMql.removeEventListener("change", this.#onMqlChange);
    window.removeEventListener("load", this.#onWindowLoadForFetchPriority);
    window.removeEventListener("resize", this.#onWindowResizeForFetchPriority);
    this.#disconnectProductGridFetchObserver();
    this.#disconnectExtendedGalleryFetchObserver();
    super.disconnectedCallback();
  }

  #restoreLayout() {
    const savedValue = sessionStorage.getItem("product-grid-view");
    if (!savedValue || savedValue === "default") return;

    this.#setLayout(savedValue);

    const matchingInput = this.querySelector(`input[name="grid"][value="${savedValue}"]`);
    if (matchingInput instanceof HTMLInputElement) {
      matchingInput.checked = true;
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreLayout();
    this.#syncProductGridImagePriority();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const grids = this.#getProductGrids();

    if (grids.length === 0) return;

    await startViewTransition(() => this.#setLayout(value), ["product-grid"]);

    requestIdleCallback(() => {
      sessionStorage.setItem("product-grid-view", value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const grids = this.#getProductGrids();
    if (grids.length === 0) return;

    for (const grid of grids) {
      const gridView = grid.dataset.productGridView || "default";
      grid.hidden = gridView !== value;
      grid.setAttribute("product-grid-view", gridView);
    }

    this.#syncProductGridImagePriority();
  }

  /**
   * Product grid `ref` can be omitted from `this.refs` when another Component sits between
   * `results-list` and the grid; fall back to stable attributes.
   *
   * @returns {HTMLElement[]}
   */
  #getProductGrids() {
    const grids = Array.from(this.querySelectorAll("[data-product-grid]")).filter(
      (grid) => grid instanceof HTMLElement
    );
    if (grids.length > 0) return grids;

    const { grid } = this.refs;
    if (grid instanceof HTMLElement) return [grid];

    const testIdGrid = this.querySelector('[data-testid="product-grid"]');
    if (testIdGrid instanceof HTMLElement) return [testIdGrid];

    return [];
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getActiveProductGrid() {
    const grids = this.#getProductGrids();
    return grids.find((grid) => !grid.hidden) || grids[0] || null;
  }

  /**
   * SSR uses desktop first-row hints (4 / 6). Below md, demote extras to lazy + auto;
   * extended first product (one-col): dual carousel/mosaic `<img>` in Liquid sets fetchpriority (data-extended-lcp-boost);
   * observer reasserts other cards after morph / subtree updates.
   */
  #syncProductGridImagePriority() {
    const grid = this.#getActiveProductGrid();
    if (!(grid instanceof HTMLElement)) return;

    const view = grid.getAttribute("product-grid-view") || "default";
    const mdUp = window.matchMedia(PRODUCT_GRID_MD_MEDIA).matches;
    const lgUp = window.matchMedia(PRODUCT_GRID_LG_MEDIA).matches;

    const cards = grid.querySelectorAll(":scope > li.product-card__item, :scope > li.product-grid__item");
    const cardList = Array.from(cards);

    for (let i = 0; i < cardList.length; i++) {
      const li = cardList[i];
      if (li.getAttribute("data-page") !== "1") continue;

      const cardIndex = i + 1;

      if (view === "one-col") {
        this.#syncExtendedGalleryImages(li);
        continue;
      }

      const isCompact = view === "zoom-out";
      const slotSelector = isCompact ? ".product-grid__card-compact" : ".product-grid__card-default";
      const gridColumnCount = this.#getProductGridColumnCount(grid);
      const compactRowBoostMax = gridColumnCount || (lgUp ? 6 : mdUp ? 4 : 3);
      const defaultRowBoostMax = gridColumnCount || (mdUp ? 4 : 2);
      const rowBoostMax = isCompact ? compactRowBoostMax : defaultRowBoostMax;

      const mainImg = li.querySelector(`${slotSelector} [data-card-main-image] img`);
      if (mainImg instanceof HTMLImageElement) {
        if (cardIndex <= rowBoostMax) {
          this.#setImgLoading(mainImg, "eager");
          this.#setImgFetchPriority(mainImg, "high");
        } else {
          this.#setImgLoading(mainImg, "lazy");
          this.#setImgFetchPriority(mainImg, "auto");
        }
      }

      const hoverImg = li.querySelector(`${slotSelector} [data-card-hover-image] img`);
      if (hoverImg instanceof HTMLImageElement) {
        if (cardIndex <= rowBoostMax) {
          this.#setImgLoading(hoverImg, "eager");
          this.#setImgFetchPriority(hoverImg, "high");
        } else {
          this.#setImgLoading(hoverImg, "lazy");
          this.#setImgFetchPriority(hoverImg, "low");
        }
      }
    }

    this.#bindExtendedGalleryFetchPriorityObserver(grid, view, cardList);
    this.#bindProductGridFetchPriorityObserver(grid);
  }

  /**
   * Uses the rendered CSS grid as the source of truth for compact first-row image priority.
   *
   * @param {HTMLElement} grid
   * @returns {number}
   */
  #getProductGridColumnCount(grid) {
    const gridTemplateColumns = window.getComputedStyle(grid).gridTemplateColumns;
    if (!gridTemplateColumns || gridTemplateColumns === "none") return 0;

    return gridTemplateColumns.split(" ").filter(Boolean).length;
  }

  #disconnectExtendedGalleryFetchObserver() {
    this.#extendedGalleryFetchObserver?.disconnect();
    this.#extendedGalleryFetchObserver = undefined;
  }

  #disconnectProductGridFetchObserver() {
    this.#productGridFetchObserver?.disconnect();
    this.#productGridFetchObserver = undefined;
    this.#productGridFetchObserverGrid = undefined;

    if (this.#productGridFetchPrioritySyncFrame) {
      cancelAnimationFrame(this.#productGridFetchPrioritySyncFrame);
      this.#productGridFetchPrioritySyncFrame = undefined;
    }
  }

  /**
   * Re-applies image priorities after third-party scripts replace card markup.
   *
   * @param {HTMLElement} grid
   */
  #bindProductGridFetchPriorityObserver(grid) {
    if (this.#productGridFetchObserverGrid === grid) return;

    this.#disconnectProductGridFetchObserver();
    this.#productGridFetchObserverGrid = grid;

    const scheduleSyncFromObserver = () => {
      if (this.#productGridFetchPrioritySyncFrame) return;

      this.#productGridFetchPrioritySyncFrame = requestAnimationFrame(() => {
        this.#productGridFetchPrioritySyncFrame = undefined;
        this.#syncProductGridImagePriority();
      });
    };

    this.#productGridFetchObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList" || mutation.type === "attributes") {
          scheduleSyncFromObserver();
          return;
        }
      }
    });

    this.#productGridFetchObserver.observe(grid, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-page", "src", "srcset", "sizes", "loading", "fetchpriority"]
    });
  }

  /**
   * Keeps first-product extended gallery fetchpriority in sync after DOM updates.
   *
   * @param {HTMLElement} grid
   * @param {string} view
   * @param {Element[]} cardList
   */
  #bindExtendedGalleryFetchPriorityObserver(grid, view, cardList) {
    this.#disconnectExtendedGalleryFetchObserver();

    if (view !== "one-col" || cardList.length === 0) return;

    const firstProductLi = cardList.find((li) => li.getAttribute("data-page") === "1");
    if (!firstProductLi) return;

    const extendedSlot = firstProductLi.querySelector(".product-grid__card-extended");
    if (!(extendedSlot instanceof HTMLElement)) return;

    const boostedGallery = firstProductLi.querySelector(
      ".product-grid__card-extended ul[data-card-images][data-extended-lcp-boost]"
    );
    if (boostedGallery instanceof HTMLElement) return;

    const reassertExtendedGalleryFetchPriority = () => {
      if (!this.isConnected) return;
      const g = this.#getActiveProductGrid();
      if (!(g instanceof HTMLElement)) return;
      const v = g.getAttribute("product-grid-view") || "default";
      if (v !== "one-col") return;

      const cards = Array.from(g.querySelectorAll(":scope > li.product-card__item, :scope > li.product-grid__item"));
      const firstLi = cards.find((li) => li.getAttribute("data-page") === "1");
      const liveGallery = firstLi?.querySelector(".product-grid__card-extended ul[data-card-images]");
      if (!(liveGallery instanceof HTMLElement)) return;
      if (liveGallery.dataset.extendedLcpBoost === "true") return;

      const imgs = liveGallery.querySelectorAll(":scope > li img, :scope > li picture img");
      imgs.forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        this.#setImgLoading(img, "lazy");
        this.#setImgFetchPriority(img, "auto");
      });
    };

    const scheduleReassertFromObserver = () => {
      queueMicrotask(reassertExtendedGalleryFetchPriority);
    };

    this.#extendedGalleryFetchObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "childList") {
          scheduleReassertFromObserver();
          return;
        }
        if (m.type === "attributes" && m.attributeName === "fetchpriority") {
          scheduleReassertFromObserver();
          return;
        }
      }
    });
    this.#extendedGalleryFetchObserver.observe(extendedSlot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["fetchpriority"]
    });

    queueMicrotask(reassertExtendedGalleryFetchPriority);
    setTimeout(reassertExtendedGalleryFetchPriority, 0);
    setTimeout(reassertExtendedGalleryFetchPriority, 120);
    setTimeout(reassertExtendedGalleryFetchPriority, 600);
  }

  /**
   * @param {Element} li
   */
  #syncExtendedGalleryImages(li) {
    const slot = li.querySelector(".product-grid__card-extended");
    if (!slot) return;

    const galleryRoot = slot.querySelector("ul[data-card-images]");
    if (!(galleryRoot instanceof HTMLElement)) return;

    if (galleryRoot.dataset.extendedLcpBoost === "true") return;

    const imgs = slot.querySelectorAll("ul[data-card-images] li img, ul[data-card-images] li picture img");
    imgs.forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      this.#setImgLoading(img, "lazy");
      this.#setImgFetchPriority(img, "auto");
    });
  }

  /**
   * @param {HTMLImageElement} img
   * @param {"eager" | "lazy"} loading
   */
  #setImgLoading(img, loading) {
    if (img.loading === loading) return;

    img.loading = loading;
  }

  /**
   * @param {HTMLImageElement} img
   * @param {"high" | "low" | "auto"} priority
   */
  #setImgFetchPriority(img, priority) {
    if (img.getAttribute("fetchpriority") === priority && img.fetchPriority === priority) return;

    img.fetchPriority = priority;
    img.setAttribute("fetchpriority", priority);
  }
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
